var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import services from 'services';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { monoColumn, Muted, mutedColumn, nameColumn, showColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, ListExportButton, listPager, usePagedRows } from 'library/components/ListScreen';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { MedicalMachine as MedicalMachineResource } from 'constants/resources';
var csvEscape = function (value) {
    var text = value.replace(/"/g, '""');
    return "\"".concat(text, "\"");
};
var downloadCsv = function (filename, rows) {
    var content = rows.map(function (row) { return row.map(csvEscape).join(','); }).join('\n');
    var blob = new Blob(["\uFEFF".concat(content)], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};
var clinicNames = function (machine) {
    return machine
        .getClinicMedicalMachines()
        .map(function (link) { var _a; return (_a = link.getClinic()) === null || _a === void 0 ? void 0 : _a.getName(); })
        .filter(function (name) { return Boolean(name); });
};
var MedicalMachines = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var medicalMachineService = services.medicalMachineService, permissionService = services.permissionService;
    var _a = medicalMachineService.useMedicalMachines(), data = _a.data, isLoading = _a.isLoading;
    var canAdd = permissionService.hasPermissionToAdd(MedicalMachineResource);
    var canShow = permissionService.hasPermissionToShow(MedicalMachineResource);
    var _b = useState(''), searchValue = _b[0], setSearchValue = _b[1];
    var _c = useState(false), exporting = _c[0], setExporting = _c[1];
    var debounceRef = useRef(undefined);
    var machines = data || [];
    var filtered = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        if (!query)
            return machines;
        return machines.filter(function (machine) {
            var _a;
            var haystack = __spreadArray([
                machine.getName(),
                machine.getOriginCountry(),
                (_a = machine.getUnitMeasurement()) === null || _a === void 0 ? void 0 : _a.getName()
            ], clinicNames(machine), true).join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [machines, searchValue]);
    var _d = usePagedRows(filtered), page = _d.page, setPage = _d.setPage, pages = _d.pages, from = _d.from, to = _d.to, pageRows = _d.pageRows;
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () {
            setPage(1);
            setSearchValue(value);
        }, 300);
    };
    var openMachine = useCallback(function (id) {
        if (!canShow)
            return;
        navigate("/medical-machines/".concat(id));
    }, [canShow, navigate]);
    var exportMachines = function () {
        if (exporting)
            return;
        if (!filtered.length) {
            message.warning(t('exportEmpty'));
            return;
        }
        setExporting(true);
        try {
            var rows = __spreadArray([
                [t('id'), t('name'), t('originCountry'), t('measurement'), t('clinics')]
            ], filtered.map(function (machine) {
                var _a;
                return [
                    String(machine.getId()),
                    machine.getName() || '',
                    machine.getOriginCountry() || '',
                    ((_a = machine.getUnitMeasurement()) === null || _a === void 0 ? void 0 : _a.getName()) || '',
                    clinicNames(machine).join('; ')
                ];
            }), true);
            var stamp = new Date().toISOString().slice(0, 10);
            downloadCsv("medical-machines-".concat(stamp, ".csv"), rows);
        }
        catch (_a) {
            message.error(t('exportFailed'));
        }
        finally {
            setExporting(false);
        }
    };
    var columns = useMemo(function () { return __spreadArray([
        monoColumn({ key: 'id', title: t('id'), width: '72px', variant: 'id', value: function (machine) { return machine.getId(); } }),
        nameColumn({ key: 'name', title: t('name'), width: 'minmax(180px, 1.4fr)', value: function (machine) { return machine.getName(); } }),
        mutedColumn({ key: 'origin', title: t('originCountry'), width: 'minmax(140px, 1fr)', value: function (machine) { return machine.getOriginCountry(); } }),
        monoColumn({ key: 'unit', title: t('measurement'), width: 'minmax(120px, 0.8fr)', value: function (machine) { var _a; return ((_a = machine.getUnitMeasurement()) === null || _a === void 0 ? void 0 : _a.getName()) || '—'; } }),
        {
            key: 'clinics',
            title: t('clinics'),
            width: 'minmax(180px, 1.2fr)',
            render: function (machine) {
                var clinics = clinicNames(machine);
                return clinics.length ? (_jsx("div", { className: "cidx-pills", children: clinics.map(function (name) { return _jsx("span", { className: "cidx-pill", children: name }, name); }) })) : _jsx(Muted, {});
            }
        }
    ], (canShow ? [showColumn({
            title: t('action'),
            label: t('common:buttons.show'),
            onShow: function (machine) { return openMachine(machine.getId()); }
        })] : []), true); }, [t, canShow, openMachine]);
    return (_jsx(ListScreen, { title: t('title'), count: t('countBadge', { count: machines.length }), actions: (_jsxs(_Fragment, { children: [_jsx(ListExportButton, { label: t('export'), busyLabel: t('exporting'), busy: exporting, onClick: exportMachines }), canAdd && _jsx(ListAddButton, { label: t('newMachine'), onClick: function () { return navigate('/medical-machines/new'); } })] })), search: { placeholder: t('searchPlaceholder'), onChange: onSearchValueChange }, children: _jsx(DataTable, { minWidth: canShow ? '860px' : '760px', loading: isLoading, rows: pageRows, rowKey: function (machine) { return machine.getId(); }, rowTestId: function (machine) { return "machine-row-".concat(machine.getId()); }, onRowClick: canShow ? function (machine) { return openMachine(machine.getId()); } : undefined, empty: _jsx(EmptyComponent, { description: t('empty') }), columns: columns, pager: listPager({
                total: filtered.length,
                page: page,
                pages: pages,
                meta: t('showingRange', { from: from, to: to, total: filtered.length }),
                previous: t('previous'),
                next: t('next'),
                center: t('pageOf', { page: page, pages: pages }),
                setPage: setPage
            }) }) }));
};
export default localeTranslator(MedicalMachines, 'medicalMachines');
